import { useState, useEffect, useContext } from 'react';
import { GetSubscription } from '../../services/SubscriptionService';
import api from '../../AxiosConfig';
import UserContext from '../../context/UserContext';
import MainLayout from '../../layouts/MainLayout';
import LoadingSpinner from '../../components/LoadingSpinner';
import Alert from '../../components/Alert';

const PaymentPlanPage = () => {
    const { user } = useContext(UserContext);
    const [subscriptionTypes, setSubscriptionTypes] = useState([]);
    const [userSubscription, setUserSubscription] = useState(null);
    const [stripePublicKey, setStripePublicKey] = useState(null);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                try {
                    const data = await GetSubscription();
                    if (data && data.Data) {
                        const sortedData = data.Data.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
                        setSubscriptionTypes(sortedData);
                    }
                } catch (error) {
                    console.error('Error fetching subscription types:', error.message);
                }

                try {
                    const response = await api.get('/payment-config');
                    setStripePublicKey(response.data['publishable_key']);
                } catch (error) {
                    console.error('Error fetching Stripe public key:', error.message);
                }

                if (user && user.role_name === 'subscriber') {
                    try {
                        const token = localStorage.getItem("jwtToken");
                        const response = await api.get("/get-subscription", {
                            headers: {
                                Authorization: `Bearer ${token}`,
                                "Content-Type": "application/json",
                            },
                        });
                        setUserSubscription(response.data.message);
                    } catch (error) {
                        console.error("Error fetching user subscription:", error);
                    }
                }
            } finally {
                setLoading(false);
            }
        };

        if (user) {
            fetchData();
        }

        // Initialize Google Analytics
        const loadGoogleAnalytics = () => {
            const script = document.createElement('script');
            script.src = `https://www.googletagmanager.com/gtag/js?id=G-Z10ME8VFN7`;
            script.async = true;
            document.head.appendChild(script);

            script.onload = () => {
                window.dataLayer = window.dataLayer || [];
                function gtag() {
                    window.dataLayer.push(arguments);
                }
                gtag('js', new Date());
                gtag('config', 'G-Z10ME8VFN7');
            };
        };

        loadGoogleAnalytics();
    }, [user]);

    function formatDate(dateStr) {
        if (!dateStr) return null;
        return new Date(dateStr).toLocaleString('en-US', {
            timeZone: 'Asia/Singapore',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    }

    function formatDateDetailed(dateStr) {
        if (!dateStr) return null;
        return new Date(dateStr).toLocaleString('en-US', {
            timeZone: 'Asia/Singapore',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    }

    if (loading) {
        return (
            <LoadingSpinner />
        );
    }

    const shouldShowPaymentPlans = () => {
        if (!user || user.role_name !== 'subscriber' || !userSubscription) {
            return false;
        }

        const now = new Date();
        const endDate = new Date(userSubscription.end_date);

        if (userSubscription.status === 'expired') {
            return true;
        }

        if (userSubscription.status === 'cancelled') {
            return true;
        }

        if (endDate <= now) {
            return true;
        }

        return false;
    };

    const getSubscriptionStatusMessage = () => {
        if (!user || user.role_name !== 'subscriber' || !userSubscription) {
            return null;
        }

        const now = new Date();
        const startDate = new Date(userSubscription.start_date);
        const endDate = new Date(userSubscription.end_date);

        if (userSubscription.status === 'expired') {
            return {
                type: 'error',
                title: 'Subscription Expired',
                message: `Your ${userSubscription.subscription_name} subscription has expired. You can purchase a new plan below to continue accessing CONSOLE-D.`
            };
        }

        if (userSubscription.status === 'cancelled') {
            return {
                type: 'warning',
                title: 'Subscription Cancelled',
                message: `Your ${userSubscription.subscription_name} subscription has been cancelled. You can purchase a new plan below to reactivate your access to CONSOLE-D.`
            };
        }

        if (userSubscription.status === 'suspended') {
            return {
                type: 'warning',
                title: 'Subscription Suspended',
                message: `Your ${userSubscription.subscription_name} subscription is currently suspended. Please contact support to restore access.`
            };
        }

        if (startDate > now) {
            const timeUntilStart = Math.ceil((startDate - now) / (1000 * 60 * 60 * 24));
            return {
                type: 'info',
                title: 'Subscription Pending',
                message: `Your ${userSubscription.subscription_name} subscription will start in ${timeUntilStart} day${timeUntilStart !== 1 ? 's' : ''} on ${formatDateDetailed(userSubscription.start_date)}. Your subscription is scheduled and will become active soon.`
            };
        }

        if (endDate <= now) {
            return {
                type: 'error',
                title: 'Subscription Expired',
                message: `Your ${userSubscription.subscription_name} subscription expired on ${formatDateDetailed(userSubscription.end_date)}. You can purchase a new plan below to continue accessing CONSOLE-D.`
            };
        }

        if (userSubscription.status === 'active' && startDate <= now && endDate > now) {
            return {
                type: 'success',
                title: 'Active Subscription',
                message: `Your ${userSubscription.subscription_name} subscription is active and providing you access to all CONSOLE-D features.`
            };
        }

        return null;
    };

    const statusMessage = getSubscriptionStatusMessage();
    const showPaymentPlans = shouldShowPaymentPlans();

    const getCardStatusInfo = () => {
        if (!user || user.role_name !== 'subscriber' || !userSubscription) {
            return null;
        }

        const now = new Date();
        const startDate = new Date(userSubscription.start_date);
        const endDate = new Date(userSubscription.end_date);

        if (userSubscription.status === 'expired') {
            return {
                text: 'EXPIRED',
                bgColor: 'bg-red-100',
                textColor: 'text-red-800',
                borderColor: 'border-red-300'
            };
        }

        if (userSubscription.status === 'cancelled') {
            return {
                text: 'CANCELLED',
                bgColor: 'bg-gray-100',
                textColor: 'text-gray-800',
                borderColor: 'border-gray-300'
            };
        }

        if (userSubscription.status === 'suspended') {
            return {
                text: 'SUSPENDED',
                bgColor: 'bg-yellow-100',
                textColor: 'text-yellow-800',
                borderColor: 'border-yellow-300'
            };
        }

        if (startDate > now) {
            const timeUntilStart = Math.ceil((startDate - now) / (1000 * 60 * 60 * 24));
            return {
                text: `STARTS IN ${timeUntilStart} DAY${timeUntilStart !== 1 ? 'S' : ''}`,
                bgColor: 'bg-blue-100',
                textColor: 'text-blue-800',
                borderColor: 'border-blue-300'
            };
        }

        if (endDate <= now) {
            return {
                text: 'EXPIRED',
                bgColor: 'bg-red-100',
                textColor: 'text-red-800',
                borderColor: 'border-red-300'
            };
        }

        return null;
    };

    const cardStatus = getCardStatusInfo();

    const showAlert = (message, type = 'error') => {
        setAlert({ message, type });
    };

    const handleStartPlanClick = async (subscription) => {
        const token = localStorage.getItem("jwtToken");
        if (Number(subscription.price) === 0) {
            try {
                const response = await api.post("/free-trial", { headers: { Authorization: `Bearer ${token}` } });
                console.log(response)

                if (response.data.status_code === 200) {
                    window.location.href = "/payment-successful";
                } else {
                    showAlert(response.data.message || "An error occurred while claiming the free trial");
                }
            } catch (e) {
                console.error(e);
                showAlert(e.message || "An error occurred while claiming the free trial");
            }
            return;
        }

        if (stripePublicKey) {
            console.log("Stripe public key is available:", stripePublicKey);
            try {
                const response = await api.post(
                    "/checkout-session",
                    {
                        subscription_type_id: subscription._id,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (window.gtag) {
                    window.gtag("event", "begin_checkout", {
                        event_label: "Begin Checkout",
                        subscription_type_id: subscription._id,
                        value: subscription.price,
                        currency: subscription.currency,
                    });
                }

                window.location.href = response.data.url;
            } catch (error) {
                console.error(
                    "Error:",
                    error.response ? error.response.data.error : error.message
                );
                showAlert(error.response ? error.response.data.error : error.message);
            }
        } else {
            console.error("Stripe public key is not available");
        }
    };

    // For all subscribers - always show current subscription with MainLayout
    if (user && user.role_name === 'subscriber' && userSubscription) {
        return (
            <MainLayout>
                <div className="py-2 sm:px-4">
                    <div className="lg:px-12 lg:mt-12 md:mt-8 sm:px-4 md:px-6">
                        <p
                            style={{ fontFamily: "Red Hat Display" }}
                            className="mb-8 lg:text-3xl md:text-2xl sm:text-2xl"
                        >
                            Payment Plans
                        </p>

                        {/* Status message for subscribers */}
                        {statusMessage && (
                            <div className={`border-l-4 p-4 mb-8 ${statusMessage.type === 'error' ? 'bg-red-50 border-red-400' :
                                statusMessage.type === 'warning' ? 'bg-yellow-50 border-yellow-400' :
                                    statusMessage.type === 'success' ? 'bg-green-50 border-green-400' :
                                        'bg-blue-50 border-blue-400'
                                }`}>
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        {statusMessage.type === 'error' && (
                                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                        {statusMessage.type === 'warning' && (
                                            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                        {statusMessage.type === 'success' && (
                                            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                        {statusMessage.type === 'info' && (
                                            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                            </svg>
                                        )}
                                    </div>
                                    <div className="ml-3">
                                        <h3 className={`text-sm font-medium ${statusMessage.type === 'error' ? 'text-red-800' :
                                            statusMessage.type === 'warning' ? 'text-yellow-800' :
                                                statusMessage.type === 'success' ? 'text-green-800' :
                                                    'text-blue-800'
                                            }`} style={{ fontFamily: 'Red Hat Display' }}>
                                            {statusMessage.title}
                                        </h3>
                                        <div className={`mt-2 text-sm ${statusMessage.type === 'error' ? 'text-red-700' :
                                            statusMessage.type === 'warning' ? 'text-yellow-700' :
                                                statusMessage.type === 'success' ? 'text-green-700' :
                                                    'text-blue-700'
                                            }`}>
                                            <p>{statusMessage.message}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Always show current subscription for subscribers */}
                        <div className="flex flex-col gap-8">
                            <p
                                style={{ fontFamily: "Red Hat Display" }}
                                className="lg:text-2xl md:text-xl sm:text-lg"
                            >
                                Your Current Plan
                            </p>
                            <div className="flex flex-col w-full max-w-lg gap-4 px-6 py-8 bg-white border-2 border-orange-600 rounded-lg relative">
                                {/* Status indicator for restricted access */}
                                {cardStatus && (
                                    <div className="absolute -top-3 -right-3">
                                        <div className={`px-3 py-1 rounded-full border text-xs font-bold ${cardStatus.bgColor} ${cardStatus.textColor} ${cardStatus.borderColor}`}>
                                            {cardStatus.text}
                                        </div>
                                    </div>
                                )}

                                <div className="flex flex-row justify-between">
                                    <p
                                        className="lg:text-2xl md:text-xl sm:text-lg orange"
                                        style={{ fontFamily: "Red Hat Display" }}
                                    >
                                        {userSubscription.subscription_name}
                                    </p>
                                    <div className="flex flex-col items-center">
                                        <p className="lg:text-lg md:text-lg sm:text-md">
                                            <b>
                                                {userSubscription.currency} {userSubscription.price}
                                            </b>
                                        </p>
                                        <p className="justify-center text-center align-middle lg:text-xs md:text-xs sm:text-xs orange w-36">
                                            {userSubscription.end_date ? (
                                                `One-time subscription till ${formatDate(userSubscription.end_date)}`
                                            ) : userSubscription.duration_days ? (
                                                `${userSubscription.duration_days} days access`
                                            ) : (
                                                `${userSubscription.billing_frequency} subscription`
                                            )}
                                        </p>
                                    </div>
                                </div>
                                <p className="lg:text-sm md:text-sm sm:text-xs">
                                    {userSubscription.description}
                                </p>
                                <div className="flex flex-row gap-8">
                                    <div className="flex flex-col gap-2">
                                        <p className="lg:text-sm md:text-sm">
                                            <b>Start time:</b>
                                        </p>
                                        <p className="lg:text-sm md:text-sm sm:text-xs">
                                            {formatDate(userSubscription.start_date)}
                                        </p>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <p className="lg:text-sm md:text-sm">
                                            <b>End time:</b>
                                        </p>
                                        <p className="lg:text-sm md:text-sm sm:text-xs">
                                            {formatDate(userSubscription.end_date)}
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-4 pt-4 border-t border-gray-200">
                                    <a
                                        href="/subscription-plan"
                                        className="block w-full bg-orange text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors text-center"
                                        style={{ fontFamily: 'Red Hat Display' }}
                                    >
                                        View Full Subscription Details
                                    </a>
                                </div>
                            </div>
                        </div>

                        {/* Conditionally show payment plans */}
                        {showPaymentPlans && (
                            <div className="flex flex-col gap-8 mt-12">
                                <p
                                    style={{ fontFamily: "Red Hat Display" }}
                                    className="lg:text-2xl md:text-xl sm:text-lg"
                                >
                                    Available Subscription Plans
                                </p>
                                <p className="text-gray-600 lg:text-base md:text-base sm:text-sm">
                                    Select a plan to renew or upgrade your access to CONSOLE-D.
                                </p>

                                <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-6">
                                    {subscriptionTypes.map((subscription, index) => (
                                        <div key={subscription._id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 relative">
                                            {/* Popular badge */}
                                            {index === subscriptionTypes.length - 1 && (
                                                <div className="absolute -top-0 left-0 right-0 bg-orange text-white text-center py-2 text-sm font-semibold" style={{ fontFamily: 'Red Hat Display' }}>
                                                    Most Popular
                                                </div>
                                            )}

                                            {/* Header with gradient */}
                                            <div 
                                                className="px-6 py-8 text-white relative"
                                                style={{
                                                    background: index === 0 
                                                        ? 'linear-gradient(135deg, #eb5527 0%, #c44520 100%)'
                                                        : index === 1
                                                        ? 'linear-gradient(135deg, #eb5527 0%, #ff6b47 100%)'
                                                        : 'linear-gradient(135deg, #ff6b47 0%, #eb5527 100%)'
                                                }}
                                            >
                                                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Red Hat Display' }}>
                                                    {subscription.subscription_name}
                                                </h3>
                                                <p className="text-sm opacity-90">
                                                    {subscription.duration_days ? `${subscription.duration_days} days` : 'Premium Access'}
                                                </p>
                                            </div>

                                            {/* Body */}
                                            <div className="px-6 py-6 flex flex-col gap-4">
                                                {/* Price */}
                                                <div className="mb-4">
                                                    <p className="text-gray-500 text-sm mb-1">Monthly price</p>
                                                    <p className="text-2xl font-bold text-gray-900">
                                                        {subscription.currency} {subscription.price}
                                                    </p>
                                                </div>

                                                {/* Features */}
                                                <div className="flex flex-col gap-3 mb-6">
                                                    <div className="border-b border-gray-100 pb-3">
                                                        <p className="text-sm text-gray-500 mb-1">Description</p>
                                                        <p className="text-sm text-gray-800">{subscription.description}</p>
                                                    </div>
                                                    
                                                    <div className="border-b border-gray-100 pb-3">
                                                        <p className="text-sm text-gray-500 mb-1">Duration</p>
                                                        <p className="text-sm text-gray-800">
                                                            {subscription.end_date ? (
                                                                <>Expires on {new Date(subscription.end_date).toLocaleDateString()}</>
                                                            ) : subscription.duration_days ? (
                                                                <>{subscription.duration_days} days access</>
                                                            ) : (
                                                                <>Expires on 30 June 2025</>
                                                            )}
                                                        </p>
                                                    </div>

                                                    {subscription.features && subscription.features.length > 0 && (
                                                        <div className="pb-3">
                                                            <p className="text-sm text-gray-500 mb-2">Features included</p>
                                                            <ul className="space-y-1">
                                                                {subscription.features.slice(0, 3).map((feature, featureIndex) => (
                                                                    <li key={featureIndex} className="text-sm text-gray-800 flex items-start gap-2">
                                                                        <span className="text-orange text-xs mt-1">✓</span>
                                                                        {feature}
                                                                    </li>
                                                                ))}
                                                                {subscription.features.length > 3 && (
                                                                    <li className="text-sm text-gray-500">
                                                                        +{subscription.features.length - 3} more features
                                                                    </li>
                                                                )}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Purchase Button */}
                                                <div className="mt-auto">
                                                    <button
                                                        onClick={() => handleStartPlanClick(subscription)}
                                                        style={{ fontFamily: "Red Hat Display" }}
                                                        className="w-full bg-orange text-white py-3 px-4 rounded-lg hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-orange focus:ring-opacity-50 transition-all duration-200 font-semibold"
                                                    >
                                                        Start Plan
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </MainLayout>
        );
    }

    // For free users
    return (
        <>
            <MainLayout>
                <div className="py-2 sm:px-4">
                    <div className="lg:px-12 lg:mt-12 md:mt-8 sm:px-4 md:px-6">
                        <div className="text-center mb-12">
                            <h1 className="lg:text-4xl md:text-3xl sm:text-2xl orange mb-4" style={{ fontFamily: 'Red Hat Display' }}>
                                Choose Your Subscription Plan
                            </h1>
                            <p className="text-gray-600 lg:text-lg md:text-base sm:text-sm">
                                Select a plan that best fits your needs and get started with CONSOLE-D.
                            </p>
                        </div>
                        
                        <div className="grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-6">
                            {subscriptionTypes.map((subscription, index) => (
                                <div key={subscription._id} className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 relative">
                                    {/* Popular badge */}
                                    {index === subscriptionTypes.length - 1 && (
                                        <div className="absolute -top-0 left-0 right-0 bg-orange text-white text-center py-2 text-sm font-semibold" style={{ fontFamily: 'Red Hat Display' }}>
                                            Most Popular
                                        </div>
                                    )}

                                    {/* Header with gradient */}
                                    <div 
                                        className="px-6 py-8 text-white relative"
                                        style={{
                                            background: index === 0 
                                                ? 'linear-gradient(135deg, #eb5527 0%, #c44520 100%)'
                                                : index === 1
                                                ? 'linear-gradient(135deg, #eb5527 0%, #ff6b47 100%)'
                                                : 'linear-gradient(135deg, #ff6b47 0%, #eb5527 100%)'
                                        }}
                                    >
                                        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Red Hat Display' }}>
                                            {subscription.subscription_name}
                                        </h3>
                                        <p className="text-sm opacity-90">
                                            {subscription.duration_days ? `${subscription.duration_days} days` : 'Premium Access'}
                                        </p>
                                    </div>

                                    {/* Body */}
                                    <div className="px-6 py-6 flex flex-col gap-4">
                                        {/* Price */}
                                        <div className="mb-4">
                                            <p className="text-gray-500 text-sm mb-1">Monthly price</p>
                                            <p className="text-2xl font-bold text-gray-900">
                                                {subscription.currency} {subscription.price}
                                            </p>
                                        </div>

                                        {/* Features */}
                                        <div className="flex flex-col gap-3 mb-6">
                                            <div className="border-b border-gray-100 pb-3">
                                                <p className="text-sm text-gray-500 mb-1">Description</p>
                                                <p className="text-sm text-gray-800">{subscription.description}</p>
                                            </div>
                                            
                                            <div className="border-b border-gray-100 pb-3">
                                                <p className="text-sm text-gray-500 mb-1">Duration</p>
                                                <p className="text-sm text-gray-800">
                                                    {subscription.end_date ? (
                                                        <>Expires on {new Date(subscription.end_date).toLocaleDateString()}</>
                                                    ) : subscription.duration_days ? (
                                                        <>{subscription.duration_days} days access</>
                                                    ) : (
                                                        <>Expires on 30 June 2025</>
                                                    )}
                                                </p>
                                            </div>

                                            {subscription.features && subscription.features.length > 0 && (
                                                <div className="pb-3">
                                                    <p className="text-sm text-gray-500 mb-2">Features included</p>
                                                    <ul className="space-y-1">
                                                        {subscription.features.slice(0, 3).map((feature, featureIndex) => (
                                                            <li key={featureIndex} className="text-sm text-gray-800 flex items-start gap-2">
                                                                <span className="text-orange text-xs mt-1">✓</span>
                                                                {feature}
                                                            </li>
                                                        ))}
                                                        {subscription.features.length > 3 && (
                                                            <li className="text-sm text-gray-500">
                                                                +{subscription.features.length - 3} more features
                                                            </li>
                                                        )}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>

                                        {/* Purchase Button */}
                                        <div className="mt-auto">
                                            <button
                                                onClick={() => handleStartPlanClick(subscription)}
                                                style={{ fontFamily: "Red Hat Display" }}
                                                className="w-full bg-orange text-white py-3 px-4 rounded-lg hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-orange focus:ring-opacity-50 transition-all duration-200 font-semibold"
                                            >
                                                Start Plan
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </MainLayout>

            {alert && (
                <Alert
                    message={alert.message}
                    type={alert.type}
                    onClose={() => setAlert(null)}
                />
            )}
        </>
    );
}

export default PaymentPlanPage;
